import { useEffect, useState } from 'react'
import {
  Modal, Button, Field, TextInput, TextArea, Select, FormGrid,
} from '@/components/ui'
import { useStore } from '@/store/useStore'
import { contactsForAccount } from '@/lib/selectors'
import { ACCOUNT_TYPE_OPTIONS, ACCOUNT_STATUS_OPTIONS } from '@/types/entities'
import type { Account, AccountType, AccountStatus, Address } from '@/types/entities'
import './AccountForm.css'

// Everything here maps 1:1 onto the editorial columns accounts-store.ts
// writes (PATCH /entities/accounts/:id). Rollups (metrics, trackIds) and the
// avatar monogram are derived at serve time and deliberately absent.
interface Draft {
  name: string
  type: AccountType
  status: AccountStatus
  phone: string
  website: string
  ticker: string
  description: string
  sourceReferredBy: string
  primaryContactId: string
  linkedIn: string
  addr: Address
}

function toDraft(a: Account): Draft {
  return {
    name: a.name ?? '',
    type: a.type,
    status: a.status,
    phone: a.phone ?? '',
    website: a.website ?? '',
    ticker: a.ticker ?? '',
    description: a.description ?? '',
    sourceReferredBy: a.sourceReferredBy ?? '',
    primaryContactId: a.primaryContactId ?? '',
    linkedIn: a.social?.linkedIn ?? '',
    addr: { ...(a.hqAddress ?? {}) },
  }
}

export function AccountForm({
  open, onClose, account,
}: { open: boolean; onClose: () => void; account: Account }) {
  const data = useStore((s) => s.data)
  const patchStore = useStore((s) => s.patch)

  const [d, setD] = useState<Draft>(() => toDraft(account))
  // Re-seed on every open so a reopened form never shows a stale draft.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setD(toDraft(account)) }, [open, account.id])
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }))
  const setAddr = (patch: Partial<Address>) => setD((p) => ({ ...p, addr: { ...p.addr, ...patch } }))

  const acctContacts = contactsForAccount(data, account.id)
  // Sync-owned rows get their name from Westport.Investors every run — an
  // edit here would silently revert within 6h, so lock the field instead.
  const nameLocked = account.synced === true
  const valid = nameLocked || d.name.trim().length > 0

  function save() {
    if (!valid) return
    const social = { ...(account.social ?? {}) }
    if (d.linkedIn.trim()) social.linkedIn = d.linkedIn.trim()
    else delete social.linkedIn

    // Explicit PATCH: empty string clears the column server-side, absent
    // fields stay untouched. `archived` changes ONLY via the dedicated
    // Archive control — carrying it here would 403 the whole edit for
    // non-crm.manage users (the API gates any write touching `archived`).
    const patch: Partial<Account> = {
      type: d.type,
      status: d.status,
      phone: d.phone.trim(),
      website: d.website.trim(),
      ticker: d.ticker.trim(),
      description: d.description.trim(),
      sourceReferredBy: d.sourceReferredBy.trim(),
      primaryContactId: d.primaryContactId,
      hqAddress: d.addr,
      social,
    }
    if (!nameLocked) patch.name = d.name.trim()
    patchStore('accounts', account.id, patch)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={680}
      title={<>Edit <b>{account.name}</b></>}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!valid}>Save changes</Button>
      </>}
    >
      <div className="aform">
        <div className="aform-sec-label">Identity</div>
        <FormGrid>
          <Field label="Name" required={!nameLocked} span={2}>
            <TextInput
              value={d.name}
              onChange={(e) => set('name', e.target.value)}
              disabled={nameLocked}
              title={nameLocked ? 'Name is managed by the SQL02 investor register' : undefined}
            />
            {nameLocked && (
              <div className="aform-hint">
                This account syncs from the SQL02 investor register — its name is source-managed
                and can’t be edited here. Everything below is editable and survives the sync.
              </div>
            )}
          </Field>
          <Field label="Type">
            <Select value={d.type} onChange={(e) => set('type', e.target.value as AccountType)}>
              {ACCOUNT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={d.status} onChange={(e) => set('status', e.target.value as AccountStatus)}>
              {ACCOUNT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Ticker"><TextInput value={d.ticker} onChange={(e) => set('ticker', e.target.value)} placeholder="e.g. BX" /></Field>
          <Field label="Primary Contact">
            <Select value={d.primaryContactId} onChange={(e) => set('primaryContactId', e.target.value)}>
              <option value="">— None —</option>
              {acctContacts.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
            </Select>
          </Field>
          <Field label="Source / Referred By" span={2}>
            <TextInput value={d.sourceReferredBy} onChange={(e) => set('sourceReferredBy', e.target.value)} />
          </Field>
        </FormGrid>

        <div className="aform-sec-label">Contact &amp; Web</div>
        <FormGrid>
          <Field label="Phone"><TextInput value={d.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(555) 555-5555" /></Field>
          <Field label="Website"><TextInput value={d.website} onChange={(e) => set('website', e.target.value)} placeholder="https://…" /></Field>
          <Field label="LinkedIn" span={2}>
            <TextInput value={d.linkedIn} onChange={(e) => set('linkedIn', e.target.value)} placeholder="https://linkedin.com/company/…" />
          </Field>
        </FormGrid>

        <div className="aform-sec-label">HQ Address</div>
        <div className="aform-addr">
          <Field label="Street"><TextInput value={d.addr.line1 ?? ''} onChange={(e) => setAddr({ line1: e.target.value })} /></Field>
          <Field label="City"><TextInput value={d.addr.city ?? ''} onChange={(e) => setAddr({ city: e.target.value })} /></Field>
          <Field label="State"><TextInput value={d.addr.state ?? ''} onChange={(e) => setAddr({ state: e.target.value })} /></Field>
          <Field label="ZIP"><TextInput value={d.addr.zip ?? ''} onChange={(e) => setAddr({ zip: e.target.value })} /></Field>
        </div>

        <div className="aform-sec-label">Description</div>
        <TextArea
          rows={3}
          value={d.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Relationship color, mandate, focus…"
        />
      </div>
    </Modal>
  )
}
